import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { finalize } from 'rxjs';
import { RilevamentoScambiDoppiService } from '@/core/service/rilevamento-doppi.service';
import { UtilsService } from '@/core/service/utils/utils.service';

@Component({
    selector: 'app-misure-cuore',
    templateUrl: './misure-cuore.component.html',
    imports: [CommonModule, ReactiveFormsModule, TabsModule],
    standalone: true
})
export class MisureCuoreComponent implements OnInit, OnChanges {
    @Input() form!: FormGroup;
    @Input() normativa: any;
    @Input() prefix: string = '';
    @Input() deviatoioId!: any;
    @Input() formReady: boolean = false;

    sKeys: string[] = ['s7', 's8'];
    extraKeys: string[] = ['p', 'q'];

    lastValues: Record<string, number> = {};
    trendIcons: Record<string, string | null> = {};
    dataReady = false;

    constructor(
        private rilevamentoService: RilevamentoScambiDoppiService,
        private utils: UtilsService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.attachValueChangeListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['deviatoioId'] && this.deviatoioId) || (changes['formReady'] && this.formReady)) {
            if (this.formReady && this.deviatoioId) {
                this.loadLastRilevamento();
            }
        }
    }

    private attachValueChangeListeners(): void {
        if (!this.form) return;

        [...this.sKeys, ...this.extraKeys].forEach((key) => {
            const control = this.form.get(this.prefix + key);
            if (!control) return;

            control.valueChanges.subscribe(() => {
                if (!this.dataReady || Object.keys(this.lastValues).length === 0) return;

                const icon = this.calculateTrendIcon(key);
                this.trendIcons[key] = icon;
                this.cdr.detectChanges();
            });
        });
    }

    private loadLastRilevamento(): void {
        this.dataReady = false;

        this.rilevamentoService
            .list({ sort: 'id,desc', size: 5 })
            .pipe(finalize(() => (this.dataReady = true)))
            .subscribe({
                next: (data) => {
                    const last = data.find((d) => d.deviatoiRiepilogoGenerale?.id === this.deviatoioId?.id);
                    if (last) {
                        this.lastValues = {
                            s7: last.cuoreInternoS7,
                            s8: last.cuoreInternoS8,
                            p: last.cuoreInternoP,
                            q: last.cuoreInternoQ
                        };

                        setTimeout(() => {
                            this.updateTrendIcons();
                            this.cdr.detectChanges();
                        }, 400);
                    } else {
                        this.lastValues = {};
                    }
                },
                error: () => (this.lastValues = {})
            });
    }

    private calculateTrendIcon(field: string): string | null {
        const lastValue = this.lastValues[field];
        if (lastValue == null) return 'pi pi-equals text-gray-400';

        const control = this.form.get(this.prefix + field);
        if (!control) return 'pi pi-equals text-gray-400';

        const current = Number(control.value);
        const prev = Number(lastValue);

        if (isNaN(current) || isNaN(prev)) return 'pi pi-equals text-gray-400';


        if (current > prev) return 'pi pi-arrow-up text-green-500';
        if (current < prev) return 'pi pi-arrow-down text-red-500';
        return 'pi pi-equals text-gray-400';
    }

    updateTrendIcons(): void {
        [...this.sKeys, ...this.extraKeys].forEach((key) => {
            const icon = this.calculateTrendIcon(key);
            this.trendIcons[key] = icon;
        });
        this.cdr.markForCheck();
    }

    getTrendIcon(field: string): string | null {
        return this.trendIcons[field] || null;
    }

    getInputRangeClass(field: string): string {
        const controlName = this.prefix.endsWith('_') ? this.prefix + field : `${this.prefix}_${field}`;
        return this.utils.getInputRangeClass(controlName, this.form, this.normativa);
    }
}
